import { useCallback, useEffect, useState } from 'react'
import { CurieSelect } from '~/components/common/CurieSelect'
import { SimpleTextarea } from '~/components/common/SimpleTextarea'
import {
  AgentRequest,
  IntegrationPlatform,
  integrationPlatformFromJSON,
  integrationPlatformToJSON,
  MigrationDocumentInput
} from '~/protos/agents'
import { bridge } from '~/services/bridge'
import {
  buildCommandContextUploadFromApiItems,
  type ContextUploadApiItem
} from '~/types/contextUpload'
import { getIntegrationPlatformItems, getIntegrationPlatformItemsForKeys } from '~/utils/agentUtils'
import { TaskParams } from './BaseTaskForm'
import {
  SupportingFilesUpload,
  type SupportingFilesUploadState
} from './shared/SupportingFilesUpload'

const MIGRATION_ACCELERATOR_DESTINATION_PLATFORM_KEYS = [
  'MULESOFT',
  'CAMEL',
  'JAVA_SPRING_BOOT',
  'INFORMATICA',
  'BOOMI',
  'AZURE_INTEGRATION_SERVICES'
]

interface MigrationAcceleratorFormProps {
  onChange: (task: TaskParams) => void
  disabled?: boolean
  initialTaskData?: TaskParams | null
}

export function MigrationAcceleratorForm({
  onChange,
  disabled = false,
  initialTaskData
}: MigrationAcceleratorFormProps) {
  // Initialize from initialTaskData if editing
  const initialPlatform =
    initialTaskData?.request?.migrationDocInput?.platform ?? IntegrationPlatform.TIBCO
  const initialDestinationPlatform =
    initialTaskData?.request?.toPlatform ?? IntegrationPlatform.MULESOFT
  const initialDetails = initialTaskData?.request?.migrationDocInput?.endpointDesc ?? ''

  const isPlugin = bridge.isInPlugin()
  const [integrationPlatform, setIntegrationPlatform] =
    useState<IntegrationPlatform>(initialPlatform)
  const [destinationPlatform, setDestinationPlatform] = useState<IntegrationPlatform>(
    initialDestinationPlatform
  )
  const [endpoint, setEndpoint] = useState<string>(initialDetails)
  const [contextUploadData, setContextUploadData] = useState<ContextUploadApiItem[]>([])
  const [submitBlockedReason, setSubmitBlockedReason] = useState<string | undefined>()

  // Update state when initialTaskData changes (e.g., when editing different tasks)
  useEffect(() => {
    if (initialTaskData) {
      const platform =
        initialTaskData.request?.migrationDocInput?.platform ?? IntegrationPlatform.TIBCO
      const toPlatform = initialTaskData.request?.toPlatform ?? IntegrationPlatform.MULESOFT
      const details = initialTaskData.request?.migrationDocInput?.endpointDesc ?? ''
      setIntegrationPlatform(platform)
      setDestinationPlatform(toPlatform)
      setEndpoint(details)
    }
  }, [initialTaskData])

  const handleSupportingFilesChange = useCallback(
    ({ contextUploadData, hasPendingUploads, hasFailedUploads }: SupportingFilesUploadState) => {
      setContextUploadData(contextUploadData)
      setSubmitBlockedReason(
        hasPendingUploads
          ? 'Please wait for all supporting files to finish uploading.'
          : hasFailedUploads
            ? 'Some supporting file uploads failed. Please remove them and try again.'
            : undefined
      )
    },
    []
  )

  useEffect(() => {
    if (integrationPlatform != null) {
      const platformText = `Source Platform: ${integrationPlatformToJSON(integrationPlatform)}`
      const destinationPlatformText = `Destination Platform: ${integrationPlatformToJSON(destinationPlatform)}`
      const detailsText = endpoint ? `\n\nDetails:\n${endpoint}` : ''
      const commandString = `${platformText}\n${destinationPlatformText}${detailsText}`
      const request = AgentRequest.fromPartial({
        command: {
          commandString,
          contextUpload: buildCommandContextUploadFromApiItems(contextUploadData)
        },
        toPlatform: destinationPlatform,
        migrationDocInput: MigrationDocumentInput.fromPartial({
          platform: integrationPlatform,
          endpointDesc: endpoint
        })
      })
      onChange?.({ request, submitBlockedReason })
    }
  }, [
    contextUploadData,
    destinationPlatform,
    endpoint,
    integrationPlatform,
    onChange,
    submitBlockedReason
  ])

  return (
    <div className='input-output-form'>
      <div className='description'>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] select-none'>
          Source Integration Platform:
        </div>
        <CurieSelect
          placeholder='Select source platform'
          ariaLabel='Source Integration Platform'
          className={isPlugin ? 'w-full pb-0' : 'w-64 pb-0'}
          selectedKeys={[integrationPlatformToJSON(integrationPlatform)]}
          items={getIntegrationPlatformItems(
            ['UI_PATH_EXP_V1', 'APIGEE_POLICY_V1', 'FLEX_GATEWAY_RUST_POLICY'],
            { includeMuleSoft: true }
          )}
          onSelectionChange={selectedKeys => {
            if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
              return
            }
            const key = selectedKeys.values().next().value as string
            setIntegrationPlatform(integrationPlatformFromJSON(key))
          }}
          disabled={disabled}
        />
      </div>
      <div className='description'>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] select-none'>
          Destination Platform:
        </div>
        <CurieSelect
          placeholder='Select destination platform'
          ariaLabel='Destination Platform'
          className={isPlugin ? 'w-full pb-0' : 'w-64 pb-0'}
          selectedKeys={[integrationPlatformToJSON(destinationPlatform)]}
          items={getIntegrationPlatformItemsForKeys(
            MIGRATION_ACCELERATOR_DESTINATION_PLATFORM_KEYS
          )}
          onSelectionChange={selectedKeys => {
            if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
              return
            }
            const key = selectedKeys.values().next().value as string
            setDestinationPlatform(integrationPlatformFromJSON(key))
          }}
          disabled={disabled}
        />
      </div>
      <div className='description'>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] select-none'>
          Details:
        </div>
        <SimpleTextarea
          className='mx-0'
          onChange={setEndpoint}
          minRows={2}
          defaultValue={initialDetails}
          disabled={disabled}
        />
        <div className='mt-4'>
          <SupportingFilesUpload onChange={handleSupportingFilesChange} disabled={disabled} />
        </div>
      </div>
    </div>
  )
}
